import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { HorizontalOptions } from '@cmps/ng-chart';
import { TranslateService } from '@ngx-translate/core';
import { max } from 'd3-array';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { CmpsDatePipe } from '../common/date.pipe';
import { ModalWebFormComponent } from '../webform/modal-webform.component';
import { WidgetConfiguration } from '../widget/config.widget.model';
import { ModalWidgetComponent } from '../widget/modal.widget.component';
import { CMPS_WIDGET_CONFIG } from '../widget/widget.metadata';
import { WidgetService } from '../widget/widget.service';
import {
    createWebFormsScorecardUrl,
    generateScorecardModalHeader,
} from '../widget/widget.util';

import { TranslationKeyMap } from './legacy-widget.i18n';
import { LegacyWidgetService } from './legacy-widget.service';
import { ScorecardListWidgetComponent } from './scorecard-list.widget.component';
import { ScorecardListPopupItem } from './scorecard-list.widget.model';
import { TeamScorecardChart } from './team-scorecard-chart.widget.model';

@Component({
    selector: 'cmps-widget-team-scorecard-chart',
    templateUrl: './team-scorecard-chart.widget.component.html',
})
export class TeamScorecardChartWidgetComponent implements OnInit, OnDestroy {
    i18n = TranslationKeyMap;
    hasPendingData: boolean;
    options: HorizontalOptions;
    data: TeamScorecardChart;
    private _modal: NgbModalRef;

    constructor(
        private readonly _modalService: NgbModal,
        private readonly _translateService: TranslateService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        @Inject(CMPS_WIDGET_CONFIG) public config: WidgetConfiguration,
        private readonly _widgetService: WidgetService,
        private readonly _service: LegacyWidgetService,
        private readonly _datePipe: CmpsDatePipe,
    ) {}

    ngOnInit(): void {
        this.options = {
            rect: { width: '100%', height: 250, right: 10 },
            showValues: true,
            xAxis: (xAxis) =>
                xAxis.ticks(max(this.data.d3.data[0].values, (d: any) => d.y)),
        };

        this.hasPendingData = true;
        this._service
            .teamScorecardChart()
            // eslint-disable-next-line no-return-assign
            .subscribe(
                (data) => (this.data = data),
                // eslint-disable-next-line n/handle-callback-err, no-return-assign
                (error) => (this.hasPendingData = false),
                // eslint-disable-next-line no-return-assign
                () => (this.hasPendingData = false),
            );
    }

    ngOnDestroy(): void {
        if (this._modal) {
            this._modal.close();
        }
    }

    showDetails(): void {
        this._modal = this._modalService.open(ModalWidgetComponent, {
            size: 'lg',
        });
        this._modal.componentInstance.config = {
            header: this.config.title,
            component: ScorecardListWidgetComponent,
        };
        this._modal.result.then(
            (result) => {
                this._modal = null;
                if (typeof result === 'object') {
                    this._showScorecard(result.item);
                }
                // eslint-disable-next-line no-return-assign
            },
            (reason) => (this._modal = null),
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _showScorecard(item: ScorecardListPopupItem) {
        const header = generateScorecardModalHeader(
            this._translateService,
            this._datePipe,
            item.username,
            item.startDate,
            item.endDate,
        );
        const url = `${
            this._appConfig.WEB_FORMS_BASE_URL
        }${createWebFormsScorecardUrl(this._datePipe, {
            id: item.scorecardId,
            name: item.scorecardName,
            userId: item.userId,
            username: item.username,
            startDate: item.startDate,
            endDate: item.endDate,
            periodTypeId: this._widgetService.dateRange.value.type,
        })}`;

        this._modal = this._modalService.open(ModalWebFormComponent, {
            windowClass: 'cmps-iframe-scorecard',
        });
        this._modal.componentInstance.config = {
            header,
            currentUrl: url,
        };
        this._modal.componentInstance.hasScrolling = false;
        // eslint-disable-next-line no-return-assign
        this._modal.result.then(
            (result) => (this._modal = null),
            (reason) => (this._modal = null),
        );
    }
}
